import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { oauthService } from '../services/oauthService';
import {
  getFieldConfigForRole,
  getFieldConfigForCurrentUser,
  getNavConfigForRole,
  getNavConfigForCurrentUser,
  EntityFieldConfig,
  NavigationConfig,
} from '../components/UpdateWizard/fieldConfig';
import { EntityType } from '../components/UpdateWizard/types';
import { useRbac } from '../auth/rbac';

interface FieldConfigContextType {
  fieldConfig: EntityFieldConfig;
  navigationConfig: NavigationConfig;
  userRoles: string[];
  /** Context-aware: checks if a field is disabled based on the RBAC active role */
  isFieldDisabled: (entityType: EntityType, fieldName: string) => boolean;
  /** Context-aware: checks if entity selection is disabled based on the RBAC active role */
  isEntitySelectionDisabled: (entityType: EntityType) => boolean;
  /** Context-aware: checks if a navigation item is disabled based on the RBAC active role */
  isNavDisabled: (navKey: string) => boolean;
}

const FieldConfigContext = createContext<FieldConfigContextType | undefined>(undefined);

export const FieldConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use the RBAC active role so field config updates when the user switches roles.
  const { activeRole, oktaRoles } = useRbac();

  const userRoles = oktaRoles;

  // Resolve field config based on the RBAC active role (not all roles).
  // This ensures switching roles changes what fields are editable.
  const fieldConfig = useMemo(
    () => activeRole ? getFieldConfigForRole(activeRole) : getFieldConfigForCurrentUser(),
    [activeRole],
  );

  const navigationConfig = useMemo(
    () => activeRole ? getNavConfigForRole(activeRole) : getNavConfigForCurrentUser(),
    [activeRole],
  );

  const isFieldDisabled = useCallback(
    (entityType: EntityType, fieldName: string): boolean => {
      const config = fieldConfig[entityType];
      if (!config || !config[fieldName]) { return false; }
      return config[fieldName].disabled;
    },
    [fieldConfig],
  );

  const isEntitySelectionDisabled = useCallback(
    (entityType: EntityType): boolean => {
      return isFieldDisabled(entityType, 'root');
    },
    [isFieldDisabled],
  );

  const isNavDisabled = useCallback(
    (navKey: string): boolean => {
      if (!navigationConfig || !navigationConfig[navKey]) { return false; }
      return navigationConfig[navKey].disabled;
    },
    [navigationConfig],
  );

  return (
    <FieldConfigContext.Provider value={{ fieldConfig, navigationConfig, userRoles, isFieldDisabled, isEntitySelectionDisabled, isNavDisabled }}>
      {children}
    </FieldConfigContext.Provider>
  );
};

/**
 * Returns the memoised field config and roles for the current user.
 * Must be used inside FieldConfigProvider.
 */
export const useFieldConfig = (): FieldConfigContextType => {
  const ctx = useContext(FieldConfigContext);
  if (!ctx) {
    // Fallback: resolve on the fly if used outside provider
    const config = getFieldConfigForCurrentUser();
    const navConfig = getNavConfigForCurrentUser();
    return {
      fieldConfig: config,
      navigationConfig: navConfig,
      userRoles: oauthService.getUserRoles(),
      isFieldDisabled: (entityType: EntityType, fieldName: string) => {
        const c = config[entityType];
        if (!c || !c[fieldName]) { return false; }
        return c[fieldName].disabled;
      },
      isEntitySelectionDisabled: (entityType: EntityType) => {
        const c = config[entityType];
        if (!c || !c['root']) { return false; }
        return c['root'].disabled;
      },
      isNavDisabled: (navKey: string) => {
        if (!navConfig || !navConfig[navKey]) { return false; }
        return navConfig[navKey].disabled;
      },
    };
  }
  return ctx;
};
